import type {
    CheckboxRowOption,
    ChipOption,
    OptionCardOption,
    StyleTileOption,
} from '@rocco/components/questionnaire';
import type { GetMatchedStyleImage } from '~/modules/get-matched/api/get-matched-style-images.server';
import styleCoastal from './images/style-coastal.jpg';
import styleContemporary from './images/style-contemporary.jpg';
import styleHeritage from './images/style-heritage.jpg';
import styleMinimalist from './images/style-minimalist.jpg';
import styleOrganic from './images/style-organic.jpg';
import styleSustainable from './images/style-sustainable.jpg';

/**
 * The answer vocabularies, in one place.
 *
 * These lists were previously duplicated across the step components, the
 * variant config, the PHP allow-lists and the CMS style records, with nothing
 * keeping them in step. Anything the server also validates should be sourced
 * from here so a new option is added once.
 */

export const USER_TYPE_OPTIONS: OptionCardOption[] = [
    {
        description: 'Planning or renovating my own home',
        title: 'Homeowner',
        value: 'Homeowner',
    },
    {
        description: 'Specifying products for client projects',
        title: 'Architect',
        value: 'Architect',
    },
    {
        description: 'Sourcing finishes, furniture and fittings',
        title: 'Interior Designer',
        value: 'Interior Designer',
    },
    {
        description: 'Sourcing products for builds at scale',
        title: 'Developer',
        value: 'Developer',
    },
    {
        description: 'Sourcing materials or components',
        title: 'Manufacturer / Supplier',
        value: 'Manufacturer / Supplier',
    },
    {
        description: 'Builder, engineer or other trade',
        title: 'Industry Professional',
        value: 'Industry Professional',
    },
];

export const PROJECT_TYPE_OPTIONS: OptionCardOption[] = [
    {
        description: 'Building from the ground up',
        title: 'New Build',
        value: 'New Build',
    },
    {
        description: 'Updating an existing space',
        title: 'Renovation',
        value: 'Renovation',
    },
    {
        description: 'Adding to an existing building',
        title: 'Extension',
        value: 'Extension',
    },
    {
        description: 'Fit-out, finishes or furnishing',
        title: 'Interior Design',
        value: 'Interior Design',
    },
    {
        description: 'Office, hospitality, retail or mixed-use',
        title: 'Commercial Project',
        value: 'Commercial Project',
    },
    {
        description: 'Outdoor areas, decks and gardens',
        title: 'Landscaping Project',
        value: 'Landscaping Project',
    },
];

/**
 * Budget brackets are project-scale in both flows. The product flow asks for
 * the project's budget, not the price of the item, so the same brackets apply.
 */
export const BUDGET_OPTIONS: OptionCardOption[] = [
    {
        description: 'Modest scope',
        title: 'Under $500K',
        value: 'Under $500K',
    },
    { description: 'Mid range', title: '$500K – $1M', value: '$500K – $1M' },
    {
        description: 'Substantial project',
        title: '$1M – $2M',
        value: '$1M – $2M',
    },
    { description: 'Premium build', title: '$2M – $5M', value: '$2M – $5M' },
    { description: 'Luxury — large scale', title: '$5M+', value: '$5M+' },
    {
        description: 'Still working it out',
        title: 'Not sure yet',
        value: 'Not sure yet',
    },
];

/** Pre-selected on the who-you-need step, with the rest offered alongside. */
export const RECOMMENDED_PROFESSIONAL = {
    description:
        'For a new build, your architect leads the design and helps bring the rest of your team together.',
    title: 'Architect',
};

export const ADDITIONAL_PROFESSIONAL_OPTIONS: CheckboxRowOption[] = [
    { label: 'Architectural Designer', value: 'Architectural Designer' },
    { label: 'Builder', value: 'Builder' },
    { label: 'Interior Designer', value: 'Interior Designer' },
    { label: 'Landscape Architect', value: 'Landscape Architect' },
    { label: 'Not sure', value: 'Not sure' },
];

export const PRIORITY_OPTIONS: CheckboxRowOption[] = [
    { label: 'Design style fit', value: 'Design style fit' },
    {
        label: 'Experience with my project type',
        value: 'Experience with my project type',
    },
    { label: 'Budget fit', value: 'Budget fit' },
    { label: 'Availability & timeline', value: 'Availability & timeline' },
    {
        label: 'Sustainability & efficiency',
        value: 'Sustainability & efficiency',
    },
    { label: 'Local knowledge', value: 'Local knowledge' },
    { label: 'Great communication', value: 'Great communication' },
    { label: 'Award-winning work', value: 'Award-winning work' },
];

export const START_TIMING_OPTIONS: ChipOption[] = [
    { label: 'Within 1-3 months', value: 'Within 1-3 months' },
    { label: 'In 3–6 months', value: 'In 3–6 months' },
    { label: '6+ months', value: '6+ months' },
    { label: 'Just exploring', value: 'Just exploring' },
];

export const CONTACT_URGENCY_OPTIONS: ChipOption[] = [
    { label: 'Right away', value: 'Right away' },
    { label: 'Within a week', value: 'Within a week' },
    { label: 'No rush', value: 'No rush' },
];

/**
 * The style vocabulary, with the photography the design shipped with.
 *
 * These bundled images are a floor, not the source of truth: a CMS record for a
 * style always wins. They exist so the step is never a grid of empty boxes on an
 * environment where nobody has curated it yet — local dev, a fresh team server,
 * or a demo — which is what it was before them.
 *
 * Every style carries one. Heritage went without for a while, on the grounds
 * that the design had no photograph for it either, and the tile it fell back to
 * read as an image that had failed rather than as a choice.
 *
 * Sized to match: 640px wide, cropped to the tile's own proportions so the
 * subject survives `object-cover`, and kept under 100KB. Nothing resizes these
 * — a CMS override is served at whatever size it was uploaded — so the floor is
 * the one place the weight is actually controlled.
 */
const STYLE_TILES: StyleTileOption[] = [
    {
        caption: 'Clean · Pure',
        image: styleMinimalist,
        name: 'MINIMALIST',
        value: 'Minimalist',
    },
    {
        caption: 'Bold · Current',
        image: styleContemporary,
        name: 'CONTEMPORARY',
        value: 'Contemporary',
    },
    {
        caption: 'Natural · Fluid',
        image: styleOrganic,
        name: 'ORGANIC',
        value: 'Organic',
    },
    {
        caption: 'Light · Open',
        image: styleCoastal,
        name: 'COASTAL',
        value: 'Coastal',
    },
    {
        caption: 'Craft · Timeless',
        image: styleHeritage,
        name: 'HERITAGE',
        value: 'Heritage',
    },
    {
        caption: 'Low impact',
        image: styleSustainable,
        name: 'SUSTAINABLE',
        value: 'Sustainable',
    },
];

/**
 * Pairs the style vocabulary with whatever photography the CMS holds for it,
 * falling back to the bundled image for any style the CMS has not curated.
 *
 * A style with neither still renders — the tile falls back to its ground, which
 * is a duller card rather than a missing answer.
 */
export const buildStyleTiles = (
    images?: GetMatchedStyleImage[],
): StyleTileOption[] => {
    const byStyle = new Map(
        (images ?? [])
            .filter((entry) => entry.imageUrl)
            .map((entry) => [entry.style.toLowerCase(), entry.imageUrl!]),
    );

    return STYLE_TILES.map((tile) => ({
        ...tile,
        image: byStyle.get(tile.value.toLowerCase()) ?? tile.image,
    }));
};

interface ProjectScopeOption {
    value: string;
    label: string;
}

/**
 * Mirrors the residential and commercial scopes in `BuildHubStageConfigService`.
 * The slugs are stored on the submission byte-identically to BuildHub's own, so
 * a brief can provision or join a project without a translation table. Internal
 * (ArchiPro-staff) scopes are excluded.
 */
export const PROJECT_SCOPE_OPTIONS: ProjectScopeOption[] = [
    { label: 'New Home Build', value: 'new-home-build' },
    { label: 'Home Renovation', value: 'home-renovation' },
    { label: 'Home Extension', value: 'extension-addition' },
    { label: 'Interior Fit-Out', value: 'interior-fitout' },
    { label: 'Landscaping', value: 'landscaping-outdoor' },
    { label: 'Pool or Spa Installation', value: 'pool-spa' },
    { label: 'New Commercial Build', value: 'new-commercial-build' },
    { label: 'Fit-Out / Refurbishment', value: 'fitout-refurbishment' },
    { label: 'Hospitality', value: 'hospitality' },
    { label: 'Retail / Showroom', value: 'retail-showroom' },
    { label: 'Office / Workplace', value: 'office-workplace' },
    { label: 'Multi-Unit Development', value: 'multi-unit-development' },
];

/** Slug to label, for rendering stored values in admin, tracking and email. */
export const PROJECT_SCOPE_LABELS: Record<string, string> = Object.fromEntries(
    PROJECT_SCOPE_OPTIONS.map((option) => [option.value, option.label]),
);
